import { useEffect, useState } from 'react';

import { addDays, isWithinInterval, startOfToday } from 'date-fns';
import { DutyCodePeriodRequest } from 'generated';
import { useDispatch, useSelector } from 'hooks';
import { difference, orderBy, sortBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { RecoilRoot, useRecoilState, useRecoilValue } from 'recoil';
import { fetchPlanUnits } from 'redux/actionCreators';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';

import { WorkWeek } from './types';
import { WorkAgreementDetails } from 'components/types';

import { TEXT } from 'constants/colors';

import {
  formatByLocale,
  fromISOString,
  getMaxDate,
  toLocalISOString,
  WeekDay
} from 'utils/date';

import { getWorkAgreementDetails } from 'RestAPIs/employees/work-agreements';
import { queries } from 'RestAPIs/rapini';
import { getWorkPatterns, WorkPatternResponse } from 'RestAPIs/work-patterns';

import {
  hasChangesState,
  isEditModeState,
  isEmployeePortalState,
  workPatternsState
} from './atoms';

import Button from '@mui/material/Button';

import { Accordion, showSnackbar } from 'components/shared';

import WorkPattern from './WorkPattern';
import { Icon } from '@sdb/planning-ui';

const Styles = styled.div`
  .custom-btn {
    text-transform: none;
    color: ${TEXT};
    font-weight: normal;
    font-style: italic;
    font-family: 'SourceSansPro-Regular';
    padding: 2px 6px;
  }

  .add-workpattern {
    margin-right: 134px;
  }
  .show-history {
    margin-top: 20px;
  }
`;

type Props = {
  employeeId?: number;
  isEmployeePortal: boolean;
  canEdit: boolean;
};

const WorkPatternPage = ({ employeeId, isEmployeePortal, canEdit }: Props) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const { publicPlanUnits, userDetails } = useSelector((state) => state);
  const { employee } = userDetails;
  const [workPatterns, setWorkPatterns] = useRecoilState(workPatternsState);
  const [isEditMode, setInEditMode] = useRecoilState(isEditModeState);
  const [, setIsEmployeePortal] = useRecoilState(isEmployeePortalState);
  const hasChanges = useRecoilValue(hasChangesState);
  const [historyWorkPatterns, setHistoryWorkPatterns] = useState<
    WorkPatternResponse[]
  >([]);
  const [expandedWorkPattern, setExpandedWorkPattern] =
    useState<WorkPatternResponse | null>(null);
  const [workAgreements, setWorkAgreements] = useState<WorkAgreementDetails[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(true);
  const { data: dutyHourTypes } = queries.useGetDutyHourTypes();

  useEffect(() => {
    setIsEmployeePortal(isEmployeePortal);
  }, [isEmployeePortal]);

  const dataIsFetched = Boolean(
    dutyHourTypes?.length && publicPlanUnits.length && workAgreements.length
  );

  const createNewWorkPattern = (): WorkPatternResponse => ({
    id: null,
    employeeId: employeeId || employee.empId,
    startDate: toLocalISOString(
      new Date(
        Math.max(
          +startOfToday(),
          workAgreements.length
            ? Math.min(
                ...workAgreements.map((wa) =>
                  Math.min(
                    ...wa.benefits.map(
                      (benefit) => +fromISOString(benefit.validFrom)
                    )
                  )
                )
              )
            : 0
        )
      )
    ),
    numberOfWeeks: 1,
    duties: []
  });

  const addWorkPattern = () => {
    const newWorkPattern = createNewWorkPattern();
    setWorkPatterns([
      ...workPatterns.filter(
        ({ employeeId: wpEmployeeId }) => employeeId === wpEmployeeId
      ),
      newWorkPattern
    ]);
    setHistoryWorkPatterns([
      ...historyWorkPatterns.filter(
        ({ employeeId: wpEmployeeId }) => employeeId === wpEmployeeId
      )
    ]);
    setExpandedWorkPattern(newWorkPattern);
    setInEditMode(true);
  };

  const removeNewlyUnsavedPattern = () => {
    setWorkPatterns(workPatterns.filter(({ id }) => id));
    setExpandedWorkPattern(null);
    setInEditMode(false);
  };

  const getWorkWeeks = ({ duties, numberOfWeeks }: WorkPatternResponse) => {
    const weeks: WorkWeek[] = new Array(numberOfWeeks)
      .fill('')
      .map((v, i) => ({ nr: i + 1 }));

    duties.forEach((duty) => {
      const periods: DutyCodePeriodRequest[] = duty.periods.map((period) => {
        const startTime = startOfToday();
        let [hours, minutes] = period.startTime.split(':');
        startTime.setHours(parseInt(hours, 10), parseInt(minutes, 10));
        const endTime = startOfToday();
        [hours, minutes] = period.endTime.split(':');
        endTime.setHours(parseInt(hours, 10), parseInt(minutes, 10));

        return {
          startTime: toLocalISOString(startTime),
          endTime: toLocalISOString(endTime),
          dutyHourTypeId: period.dutyHourTypeId,
          dutyHourType: dutyHourTypes?.filter(
            ({ dutyHourTypeId }) => dutyHourTypeId === period.dutyHourTypeId
          )[0]
        };
      });

      const dayName = duty.dayOfWeek.toLowerCase() as WeekDay;
      const workDuty = {
        id: uuidv4(),
        periods,
        workAgreement: workAgreements.filter(
          (wa) => wa.waId === duty.workAgreementId
        )[0],
        planUnit: publicPlanUnits.filter(
          ({ id }) => id === duty.costCenterId
        )[0]
      };

      const week = weeks[duty.weekNumber - 1];

      let dayDuties = week[dayName];
      if (!dayDuties) {
        dayDuties = [];
        week[dayName] = dayDuties;
      }
      dayDuties.push(workDuty);
    });

    return weeks;
  };

  const showHistory = () => {
    setWorkPatterns([...historyWorkPatterns, ...workPatterns]);
    setHistoryWorkPatterns([]);
  };

  const loadWorkPatterns = () => {
    setIsLoading(true);
    getWorkPatterns(isEmployeePortal, employeeId).then(
      ({ data }) => {
        if (data.length) {
          const wps = data.map((wp) => ({
            ...wp,
            startDate: toLocalISOString(new Date(wp.startDate)),
            endDate: wp.endDate
              ? toLocalISOString(new Date(wp.endDate))
              : wp.endDate
          }));

          const tomorrow = addDays(startOfToday(), 1);

          const visibleWPs = wps.filter(
            ({ startDate, endDate }) =>
              fromISOString(startDate) >= tomorrow ||
              isWithinInterval(tomorrow, {
                start: fromISOString(startDate),
                end: endDate ? fromISOString(endDate) : getMaxDate()
              })
          );

          const historyWPs = difference(wps, visibleWPs);

          if (visibleWPs.length) {
            setWorkPatterns(visibleWPs);
            setExpandedWorkPattern(
              orderBy(
                visibleWPs,
                (wp) => fromISOString(wp.startDate),
                'asc'
              )[0] || null
            );
            setHistoryWorkPatterns(historyWPs);
          } else {
            setWorkPatterns(historyWPs);
            setHistoryWorkPatterns([]);
          }
        } else if (canEdit) {
          addWorkPattern();
        }
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  const onSelectedWorkPattern = (wp: WorkPatternResponse) => {
    if (
      (isEditMode && hasChanges) ||
      (expandedWorkPattern && !expandedWorkPattern.id)
    ) {
      if (expandedWorkPattern !== wp) {
        showSnackbar({
          message: t('unsavedChanges'),
          severity: 'info'
        });
      }
    } else if (expandedWorkPattern === wp) {
      setExpandedWorkPattern(null);
    } else {
      setExpandedWorkPattern(wp);
      if (isEditMode) {
        setInEditMode(false);
      }
    }
  };

  useEffect(() => {
    dispatch(
      fetchPlanUnits({
        usePublicPlanUnits: true
      })
    );
    setExpandedWorkPattern(null);
    setInEditMode(false);
    setWorkPatterns([]);
    if (employeeId !== undefined) {
      setIsLoading(true);
      getWorkAgreementDetails({
        employeeId
      }).then(({ data }) => {
        setWorkAgreements(sortBy(data, 'nr'));
        loadWorkPatterns();
      });
    }
  }, [employeeId]);

  return (
    <Styles>
      <div className="add-workpattern d-flex end">
        <Button
          variant="text"
          size="small"
          onClick={addWorkPattern}
          className="custom-btn"
          startIcon={<Icon type="plus" />}
          disabled={!canEdit || isLoading || isEditMode}
        >
          {t('extraWorkPattern')}
        </Button>
      </div>
      {dataIsFetched && (
        <>
          {workPatterns.length > 0 && (
            <div className="work-patterns">
              {orderBy(
                workPatterns,
                [(wp) => !wp.id, ({ startDate }) => fromISOString(startDate)],
                ['desc', 'desc']
              ).map((wp) => (
                <Accordion
                  key={wp.id}
                  disableGutters
                  summary={`${formatByLocale(new Date(wp.startDate), 'PP')}
                                                    -
                                                    ${
                                                      wp.endDate
                                                        ? formatByLocale(
                                                            new Date(
                                                              wp.endDate
                                                            ),
                                                            'PP'
                                                          )
                                                        : t('indefinite')
                                                    }`}
                  expanded={expandedWorkPattern === wp}
                  onSelected={() => onSelectedWorkPattern(wp)}
                >
                  <WorkPattern
                    workPatternId={wp.id}
                    weeks={getWorkWeeks(wp)}
                    startDate={wp.startDate}
                    endDate={wp.endDate}
                    onRefreshWorkPatterns={() => loadWorkPatterns()}
                    onCancelEdit={() => removeNewlyUnsavedPattern()}
                    workAgreements={workAgreements}
                    employeeId={employeeId}
                    canEdit={canEdit}
                  />
                </Accordion>
              ))}
            </div>
          )}
          {historyWorkPatterns.length > 0 && (
            <div className="show-history">
              <Button variant="outlined" size="small" onClick={showHistory}>
                {t('showHistory')}
              </Button>
            </div>
          )}
        </>
      )}
    </Styles>
  );
};

export default function WorkPatternPageRoot(props: Props) {
  return (
    <RecoilRoot>
      <WorkPatternPage {...props} />
    </RecoilRoot>
  );
}
